<template>
    <Card>
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_AGCO_LOG">
            <div class="agco-log-guidance" role="note">
                These logs show submission attempts and the result recorded by this application. A Success entry does not independently prove
                that AGCO accepted the submission.
            </div>
            <TabContainer @change="handleChangeTab" :tabs="Object.values(AGCO_LOG_TABS)" :selectedTab="selectedTab" />

            <div class="my-3" v-if="loading">Loading...</div>

            <CustomTable v-else>
                <template #head>
                    <tr>
                        <th>Location</th>
                        <th>Last Upload Timestamp</th>
                        <th>Status</th>
                    </tr>
                </template>
                <template #body>
                    <tr v-for="warehouseAgcoLog of warehouseAgcoLogs" :key="warehouseAgcoLog.id">
                        <td>
                            <router-link
                                class="warehouse-link"
                                :to="`/agco-logs?warehouse_id=${warehouseAgcoLog.id}&is_position_trigger_event=${isPositionTriggerEvent}`"
                                >{{ warehouseAgcoLog.warehouseName }}</router-link
                            >
                        </td>
                        <td>
                            {{ warehouseAgcoLog.agcoLog ? usDateFormat(warehouseAgcoLog.agcoLog.createdAt) : "NA" }}
                        </td>
                        <td>
                            <div
                                :class="[
                                    'status-badge',
                                    !warehouseAgcoLog.agcoLog ? 'status-na' : 
                                    warehouseAgcoLog.agcoLog.isError ? 'status-error' : 'status-success'
                                ]"
                            >
                                {{ !warehouseAgcoLog.agcoLog ? "NA" : warehouseAgcoLog.agcoLog.isError ? "Error" : "Success" }}
                            </div>
                        </td>
                    </tr>
                </template>
            </CustomTable>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions } from "vuex"
import CustomTable from "../../components/layout/CustomTable.vue"
import { http } from "../../utils/http"
import Card from "../../components/UI/Card.vue"
import { usDateFormat } from "../../utils/common"
import TabContainer from "../../components/UI/TabContainer.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"

const AGCO_LOG_TABS = {
    POST_INVENTORY_EVENT: {
        name: "POST_INVENTORY_EVENT",
        value: "Post Inventory Event",
    },
    POST_INVENTORY_POSITION: {
        name: "POST_INVENTORY_POSITION",
        value: "Post Inventory Position",
    },
}

export default {
    data() {
        return {
            warehouseAgcoLogs: [],
            totalPages: 0,
            page: 1,
            loading: true,
            limit: 10,
            selectedTab: AGCO_LOG_TABS.POST_INVENTORY_EVENT,
            AGCO_LOG_TABS,
            PERMISSION_CODES,
        }
    },
    components: {
        Card,
        CustomTable,
        TabContainer,
        AllowPermission,
    },
    mounted() {
        this.setData({
            pageTitle: "AGCO Logs",
        })
        this.fetchData()
    },
    computed: {
        isPositionTriggerEvent() {
            return this.selectedTab.name == AGCO_LOG_TABS.POST_INVENTORY_POSITION.name ? "true" : "false"
        },
    },
    methods: {
        handleChangeTab(e) {
            this.selectedTab = e
            this.fetchData()
        },
        usDateFormat,
        ...mapActions("misc", ["setData"]),
        async fetchData() {
            try {
                this.loading = true
                this.warehouseAgcoLogs = []
                const res = await http.get(`/agco-logs/warehouses?is_position_trigger_event=${this.isPositionTriggerEvent}`)
                this.warehouseAgcoLogs = res.data.result
            } catch (err) {
                console.log("error in fetching warehouseAgcoLogs", err)
            }
            this.loading = false
        },
    },
    watch: {
        page() {
            this.fetchData()
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.warehouse-link {
    color: #08f;
}

.agco-log-guidance {
    margin-bottom: $sectionGap;
    padding: 12px 14px;
    border: 1px solid rgba(127, 127, 127, 0.32);
    border-radius: 8px;
    background: rgba(127, 127, 127, 0.08);
    color: inherit;
    font-size: 14px;
    line-height: 1.45;
}

.status-badge {
    padding: 2px;
    font-size: 12px;
    border-radius: 5px;
    text-align: center;
    width: 100px;
    border: 2px solid;
}

.status-error {
    border-color: #dc3545;
    color: #dc3545;
}

.status-success {
    border-color: #28a745;
    color: #28a745;
}

.status-na {
    border-color: #6c757d;
    color: #6c757d;
}

::v-deep .tab-container.dark .tab-item {
    &.active,
    &:hover {
        border-bottom-color: $secondaryColorDark;
        color: $secondaryColorDark;
    }
}

/* Tokenized table paddings */
::deep(table) th,
::deep(table) td {
  padding: $tableCellPaddingY $tableCellPaddingX;
}

/* Dark mode overrides */
:deep(.dark) {
    .warehouse-link {
        color: $secondaryColorDark;
    }
    
    .status-error {
        border-color: #ff6b6b;
        color: #ff6b6b;
    }
    
    .status-success {
        border-color: #51cf66;
        color: #51cf66;
    }
    
    .status-na {
        border-color: #adb5bd;
        color: #adb5bd;
    }
}
</style>
